import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DeviceMobile, X, DownloadSimple } from "@phosphor-icons/react";

const DISMISS_KEY = "sep_pwa_dismissed_until";
const COOLDOWN_DAYS = 7;

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}
function isInStandaloneMode() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches ||
         window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return; // already installed

    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < dismissedUntil) return; // still in cooldown

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Delay showing 8s to not interrupt first impression
      setTimeout(() => setVisible(true), 8000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: no beforeinstallprompt, show manual instructions
    if (isIos()) {
      setTimeout(() => { setIosHint(true); setVisible(true); }, 10000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_DAYS * 86400000));
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (choice.outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_DAYS * 86400000));
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 240 }}
          className="fixed bottom-24 right-6 left-6 md:left-auto md:bottom-6 md:right-24 z-40 max-w-md md:max-w-sm"
          data-testid="install-prompt"
        >
          <div className="bg-[#0A0A0A] border border-[#FFD300]/40 shadow-2xl shadow-black/60 overflow-hidden">
            <div className="bg-[#FFD300] text-black px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold">
                <DeviceMobile size={14} weight="bold" /> Install SEP Academy
              </div>
              <button onClick={dismiss} data-testid="install-dismiss" className="hover:opacity-70 transition-opacity">
                <X size={14} />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <img src="/icon-192.png" alt="SEP" className="w-12 h-12 flex-shrink-0" />
                <div>
                  <div className="font-heading text-base tracking-tight">Add to your home screen</div>
                  <div className="text-xs text-neutral-400 mt-0.5">Faster access · Works offline · Class reminders</div>
                </div>
              </div>

              {iosHint ? (
                <div className="text-xs text-neutral-300 leading-relaxed mb-4 space-y-2">
                  <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#FFD300]">On iPhone / iPad:</p>
                  <p>1. Tap the <strong>Share</strong> button <span className="inline-block px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">⎙</span> in Safari</p>
                  <p>2. Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                  <p>3. Tap <strong>Add</strong></p>
                </div>
              ) : (
                <ul className="text-xs text-neutral-300 leading-relaxed mb-4 space-y-1.5">
                  <li>• One-tap access from your home screen</li>
                  <li>• Offline view of enrolled courses</li>
                  <li>• Class date reminders</li>
                </ul>
              )}

              <div className="flex gap-2">
                {!iosHint && deferred && (
                  <button onClick={install} data-testid="install-confirm" className="btn-primary text-xs py-2.5 px-4 flex-1 justify-center">
                    Install <DownloadSimple size={12} />
                  </button>
                )}
                <button onClick={dismiss} data-testid="install-later" className="btn-secondary text-xs py-2.5 px-4 flex-1 justify-center">
                  {iosHint || !deferred ? "Got it" : "Maybe later"}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
